/**
 * Performance Reviews API client (ADR-0013 Phase 4). Wraps /v1/performance/*
 * and /v1/me/performance/*. The visibility gate is enforced server-side — the
 * /me reads simply never carry a manager's in-progress ratings; the UI relies
 * on that, it does not re-implement it.
 */
import type {
  PerformanceCycleStatus,
  PerformanceGoalOwnerType,
  PerformanceGoalStatus,
  ReviewAssignmentType,
  ReviewResponseRatings,
  ReviewTemplateDefinition,
} from '@kora/types';
import { apiRequest } from './client.js';

// --- shapes ---------------------------------------------------------------

export interface PerfCycle {
  id: string;
  name: string;
  periodStart: string;
  periodEnd: string;
  status: PerformanceCycleStatus;
  templateId: string | null;
}

export interface PerfTemplate {
  id: string;
  name: string;
  definition: ReviewTemplateDefinition;
  createdAt: string;
}

export interface PerfGoal {
  id: string;
  cycleId: string;
  ownerType: PerformanceGoalOwnerType;
  ownerEmployeeId: string | null;
  parentGoalId: string | null;
  title: string;
  description: string | null;
  target: string | null;
  weight: string | null;
  status: PerformanceGoalStatus;
}

export interface WeightWarning {
  ownerEmployeeId: string;
  weightSum: number;
  ok: boolean;
}

export interface AssignmentListItem {
  id: string;
  cycleId: string;
  subjectEmployeeId: string;
  reviewerId: string | null;
  type: ReviewAssignmentType;
  response: { submittedAt: string | null } | null;
  cycle: { name: string; status: PerformanceCycleStatus };
}

export interface KraRef {
  id: string;
  title: string;
  description?: string | null;
  weight: string | null;
}

/** Single-assignment detail (self- or reviewer-scoped). `ratings` is PRESENT
 *  only when the caller owns that content — a subject viewing their manager
 *  assignment gets it omitted (the gate). */
export interface AssignmentDetail {
  assignment: {
    id: string;
    cycleId: string;
    cycle: { name: string; status: PerformanceCycleStatus };
    subjectEmployeeId: string;
    type: ReviewAssignmentType;
    reviewerId: string | null;
  };
  submittedAt: string | null;
  ratings?: ReviewResponseRatings | null;
  kras: KraRef[];
  template: ReviewTemplateDefinition | null;
}

export interface ReviewContext {
  subject: { firstName: string; lastName: string; employeeNumber: string; department: string | null } | null;
  kras: KraRef[];
  template: ReviewTemplateDefinition | null;
  assignmentIds: { self: string | null; manager: string };
  selfAssessment: { submittedAt: string | null; ratings: ReviewResponseRatings | null };
  managerDraft: { submittedAt: string | null; ratings: ReviewResponseRatings | null };
  outcome: PerfOutcome | null;
}

export type AcknowledgmentStatus = 'acknowledged' | 'awaiting' | 'waived';

export interface PerfOutcome {
  id: string;
  cycleId: string;
  subjectEmployeeId: string;
  kraScore: string;
  competencyScore: string;
  finalScore: string;
  recommendation: string | null;
  acknowledgedAt: string | null;
  acknowledgmentComment: string | null;
  acknowledgmentWaivedAt: string | null;
  acknowledgmentWaiveReason: string | null;
  acknowledgmentStatus: AcknowledgmentStatus;
  finalizedAt: string;
  cycle?: { name: string; status: PerformanceCycleStatus };
  subject?: { id: string; firstName: string; lastName: string; department: string | null; managerId: string | null };
}

export interface RollupResponse {
  outcomes: PerfOutcome[];
  summary: {
    count: number;
    byDepartment: Array<{ group: string; count: number; avgFinalScore: number }>;
    byManager: Array<{ group: string; count: number; avgFinalScore: number }>;
    acknowledgment: { acknowledged: number; awaiting: number; waived: number };
  };
}

// --- cycles + templates (HR) ----------------------------------------------

export const listCycles = (status?: PerformanceCycleStatus): Promise<{ cycles: PerfCycle[] }> =>
  apiRequest(`/v1/performance/cycles${status ? `?status=${status}` : ''}`);

export const createCycle = (body: { name: string; periodStart: string; periodEnd: string; templateId?: string }): Promise<PerfCycle> =>
  apiRequest('/v1/performance/cycles', { method: 'POST', body });

export const transitionCycle = (id: string, status: PerformanceCycleStatus, reason?: string): Promise<PerfCycle & { assignmentsCreated?: number }> =>
  apiRequest(`/v1/performance/cycles/${encodeURIComponent(id)}/transition`, { method: 'POST', body: { status, ...(reason ? { reason } : {}) } });

export const listTemplates = (): Promise<{ templates: PerfTemplate[] }> => apiRequest('/v1/performance/templates');

export const createTemplate = (body: { name: string; definition: ReviewTemplateDefinition }): Promise<PerfTemplate> =>
  apiRequest('/v1/performance/templates', { method: 'POST', body });

// --- goals (cascade) ------------------------------------------------------

export const listCycleGoals = (cycleId: string): Promise<{ goals: PerfGoal[]; weightWarnings: WeightWarning[] }> =>
  apiRequest(`/v1/performance/cycles/${encodeURIComponent(cycleId)}/goals`);

export const createGoal = (
  cycleId: string,
  body: { ownerType: PerformanceGoalOwnerType; ownerEmployeeId?: string; parentGoalId?: string | null; title: string; description?: string; target?: string; weight?: number },
): Promise<PerfGoal> => apiRequest(`/v1/performance/cycles/${encodeURIComponent(cycleId)}/goals`, { method: 'POST', body });

export const deleteGoal = (id: string): Promise<unknown> =>
  apiRequest(`/v1/performance/goals/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const listMyGoals = (): Promise<{ goals: PerfGoal[] }> => apiRequest('/v1/me/performance/goals');

export const agreeGoal = (id: string): Promise<PerfGoal> =>
  apiRequest(`/v1/me/performance/goals/${encodeURIComponent(id)}/agree`, { method: 'POST', body: {} });

// --- assignments + appraisal ----------------------------------------------

export const listMyAssignments = (): Promise<{ assignments: AssignmentListItem[] }> => apiRequest('/v1/me/performance/assignments');

export const getMyAssignment = (id: string): Promise<AssignmentDetail> =>
  apiRequest(`/v1/me/performance/assignments/${encodeURIComponent(id)}`);

export const submitSelfAssessment = (id: string, ratings: ReviewResponseRatings): Promise<unknown> =>
  apiRequest(`/v1/me/performance/assignments/${encodeURIComponent(id)}/response`, { method: 'POST', body: ratings });

export const submitManagerReview = (id: string, ratings: ReviewResponseRatings): Promise<unknown> =>
  apiRequest(`/v1/performance/assignments/${encodeURIComponent(id)}/response`, { method: 'POST', body: ratings });

export const getReviewContext = (cycleId: string, subjectEmployeeId: string): Promise<ReviewContext> =>
  apiRequest(`/v1/performance/cycles/${encodeURIComponent(cycleId)}/subjects/${encodeURIComponent(subjectEmployeeId)}/review`);

export const finalizeReview = (cycleId: string, subjectEmployeeId: string, recommendation?: string): Promise<PerfOutcome> =>
  apiRequest(`/v1/performance/cycles/${encodeURIComponent(cycleId)}/subjects/${encodeURIComponent(subjectEmployeeId)}/finalize`, {
    method: 'POST',
    body: { ...(recommendation ? { recommendation } : {}) },
  });

// --- outcomes + acknowledgment --------------------------------------------

export const getRollup = (cycleId: string): Promise<RollupResponse> =>
  apiRequest(`/v1/performance/cycles/${encodeURIComponent(cycleId)}/outcomes`);

export const listMyOutcomes = (): Promise<{ outcomes: PerfOutcome[] }> => apiRequest('/v1/me/performance/outcomes');

export const acknowledgeReview = (outcomeId: string, comment?: string): Promise<PerfOutcome> =>
  apiRequest(`/v1/me/performance/outcomes/${encodeURIComponent(outcomeId)}/acknowledge`, { method: 'POST', body: { ...(comment ? { comment } : {}) } });

export const waiveAcknowledgment = (outcomeId: string, reason: string): Promise<PerfOutcome> =>
  apiRequest(`/v1/performance/outcomes/${encodeURIComponent(outcomeId)}/acknowledge-waive`, { method: 'POST', body: { reason } });
